import { Koi } from '@ksyos/koi';
import type { CustomControlCode } from '@common/enums';
import type { Section } from '@common/formEngine/formEngine.types';
import type { Icd10CodeGroup } from '@common/interfaces/api/icd10Api';
import type { HolterDeviations } from '@common/validation/customControls/cardiology/holter/utils/deviationUtils';
import type { HolterGradingRelationEcgAndComplaints } from '@common/validation/customControls/cardiology/holter/utils/gradingAndConsultUtils';
import { BezoekAanSpecialistOption } from '@common/enums';
import { YesNoNoDeviations } from '@common/enums/yesNoDeviations';
import { dependsOn } from '@common/formEngine/dependsOn';
import {
    HolterDeviation,
    holterDeviationOptions,
} from '@common/validation/customControls/cardiology/holter/utils/deviationUtils';
import {
    HolterConclusionOption,
    holterConclusionOptions,
    HolterGradingAdvice,
    holterGradingAdviceOptions,
    holterGradingRelatieEcgEnKlachtenOptions,
    HolterGradingUrgency,
} from '@common/validation/customControls/cardiology/holter/utils/gradingAndConsultUtils';
import { yesNoNoDeviationsOptions } from '@common/validation/customControls/cardiology/holter/utils/utils';

export type HolterGradingData = {
    additionalFindings: string | null;
    additionalFollowUp: string | null;
    advice: HolterGradingAdvice | HolterGradingAdvice[] | null;
    adviceQuestion: string | null;
    conclusion: HolterConclusionOption | null;
    deviations: HolterDeviations;
    hasDiagnosis: YesNoNoDeviations | null;
    policy: string | null;
    relationEcgAndComplaints: HolterGradingRelationEcgAndComplaints | null;
    urgency: HolterGradingUrgency | null;
    visitSpecialist: BezoekAanSpecialistOption | null;
    workDiagnosis: Icd10CodeGroup | null;
};

export type HolterGradingContentKeys = 'adviceQuestionAlert';

export const holterGradingSections: Section<
    HolterGradingData,
    HolterGradingContentKeys,
    CustomControlCode.HolterGrading
>[] = [
    {
        fields: [
            {
                isRequired: true,
                key: 'conclusion',
                label: 'Conclusie',
                type: 'select',
                valueOptions: holterConclusionOptions,
            },
            {
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion'],
                    ({ processData, conclusion }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion)
                ),
                key: 'adviceQuestionAlert',
                type: 'content',
            },
        ],
    },
    {
        fields: [
            {
                checkboxEnumType: HolterDeviation,
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion'],
                    ({ processData, conclusion }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion)
                ),
                isRequired: true,
                key: 'deviations',
                label: 'Afwijking in',
                type: 'nestedCheckboxGroup',
                valueOptions: holterDeviationOptions,
            },
            {
                draftSchema: Koi.string()
                    .allow(null)
                    .custom((value, helpers) => {
                        const isValid = holterGradingRelatieEcgEnKlachtenOptions.some(
                            (option) => value.startsWith(option) || value.includes(option)
                        );

                        if (isValid && value.trim().length > 0) {
                            return value;
                        }

                        return helpers.error('any.invalid');
                    }),
                finalSchema: Koi.string().custom((value, helpers) => {
                    const isValid = holterGradingRelatieEcgEnKlachtenOptions.some(
                        (option) => value.startsWith(option) || value.includes(option)
                    );

                    if (isValid && value.trim().length > 0) {
                        return value;
                    }

                    return helpers.error('any.invalid');
                }),
                initialValue: null,
                isRequired: true,
                key: 'relationEcgAndComplaints',
                label: 'Relatie ECG en klachten',
                type: 'custom',
            },
            {
                draftSchema: Koi.string().max(255).allow(null),
                finalSchema: Koi.string().max(255).allow(null),
                isRequired: false,
                key: 'additionalFindings',
                label: 'Toelichting bevindingen',
                type: 'textArea',
            },
            {
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion'],
                    ({ processData, conclusion }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion)
                ),
                isRequired: true,
                key: 'hasDiagnosis',
                label: 'Diagnose bekend',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNoDeviationsOptions,
            },
            {
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion', 'hasDiagnosis'],
                    ({ processData, conclusion, hasDiagnosis }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion) &&
                        hasDiagnosis === YesNoNoDeviations.yes
                ),
                isRequired: true,
                key: 'workDiagnosis',
                label: 'Werkdiagnose',
                type: 'icd10',
            },
            {
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion', 'hasDiagnosis'],
                    ({ processData, conclusion, hasDiagnosis }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion) &&
                        hasDiagnosis === YesNoNoDeviations.yes
                ),
                isRequired: dependsOn(['hasDiagnosis'], ({ hasDiagnosis }) => hasDiagnosis === YesNoNoDeviations.yes),
                key: 'visitSpecialist',
                label: 'Bezoek aan specialist nu nodig',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: Object.values(BezoekAanSpecialistOption),
            },
        ],
        title: 'Diagnostische bevindingen',
    },
    {
        fields: [
            {
                draftSchema: Koi.string().max(255).allow(null),
                finalSchema: Koi.string().max(255).allow(null),
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion'],
                    ({ processData, conclusion }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion)
                ),
                initialValue: null,
                key: 'adviceQuestion',
                label: 'Adviesvraag',
                type: 'custom',
            },
            {
                draftSchema: Koi.alternatives()
                    .try(
                        Koi.string().valid(...holterGradingAdviceOptions),
                        Koi.array().items(Koi.string().valid(...holterGradingAdviceOptions))
                    )
                    .allow(null),
                finalSchema: Koi.alternatives().try(
                    Koi.array()
                        .items(Koi.string().valid(...holterGradingAdviceOptions))
                        .min(1),
                    Koi.string().valid(...holterGradingAdviceOptions)
                ),
                initialValue: [],
                isRequired: true,
                key: 'advice',
                label: 'Advies',
                type: 'custom',
            },
            {
                draftSchema: Koi.koi().enum(HolterGradingUrgency).allow(null),
                finalSchema: Koi.koi().custom((value, helpers) => {
                    const { advice, conclusion } = helpers.state.ancestors[0];
                    const ifConclusionIsNotAlarmerendAndAdviceIsArrayButNotIncludingUrgentAdvice =
                        conclusion !== HolterConclusionOption.alarmerend_afwijkend &&
                        Array.isArray(advice) &&
                        advice.some((selectedAdvice) =>
                            [
                                HolterGradingAdvice.verwijzing_polikliniek_cardiologie,
                                HolterGradingAdvice.aanvullend_slaaponderzoek_naar_osas_viaKsyos,
                                HolterGradingAdvice.opvolgend_teleconsult_cardioloog_via_ksyos,
                            ].includes(selectedAdvice)
                        );
                    const ifConclusionIsNotAlarmerendAndAdviceIsNull =
                        conclusion !== HolterConclusionOption.alarmerend_afwijkend && advice;

                    if (
                        (value === null && ifConclusionIsNotAlarmerendAndAdviceIsArrayButNotIncludingUrgentAdvice) ||
                        (value === null && ifConclusionIsNotAlarmerendAndAdviceIsNull)
                    ) {
                        return value;
                    }

                    if (value !== null && !Object.values(HolterGradingUrgency).includes(value)) {
                        return helpers.error('any.only');
                    }

                    if (value !== null && Object.values(HolterGradingUrgency).includes(value)) {
                        return value;
                    }
                }),
                initialValue: null,
                isRequired: true,
                key: 'urgency',
                label: 'Urgentie verwijzing',
                type: 'custom',
            },
            {
                include: dependsOn(
                    ['processData.isAdviceQuestionFilledIn', 'conclusion'],
                    ({ processData, conclusion }) =>
                        processData.isAdviceQuestionFilledIn &&
                        conclusion !== null &&
                        [
                            HolterConclusionOption.borderline,
                            HolterConclusionOption.afwijkend,
                            HolterConclusionOption.alarmerend_afwijkend,
                        ].includes(conclusion)
                ),
                isRequired: dependsOn(
                    ['visitSpecialist'],
                    ({ visitSpecialist }) => visitSpecialist === BezoekAanSpecialistOption.zieBeleid
                ),
                key: 'policy',
                label: 'Beleid',
                type: 'textInput',
            },
            {
                isRequired: false,
                key: 'additionalFollowUp',
                label: 'Toelichting vervolg',
                type: 'textArea',
            },
        ],
        title: 'Vervolg',
    },
];
